/**
 * Lightweight workflow timing. `startWorkflowTimer(name)` stamps a start;
 * `completeWorkflowTimer(name, outcome)` emits a `portal.workflow_timing` event
 * with a duration bucket (+ exact ms in the payload) and outcome. No clinical
 * attributes — only the low-cardinality workflow name.
 *
 * Decoupled from aws-rum-web via an injectable sink (rumClient registers the
 * real one in initRum), so this module imports no AWS code and is fully
 * unit-testable in the node jest env. Calls before a sink is registered, or
 * with no matching start, are safe no-ops.
 */
import { durationBucket } from './apiTelemetry';

export interface WorkflowTimingEvent {
  workflowName: string;
  outcome: string;
  durationBucket: string;
  durationMs: number;
}

type WorkflowTimingSink = (event: WorkflowTimingEvent) => void;

let sink: WorkflowTimingSink = () => {};
const starts = new Map<string, number>();

function now(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

/** Registered once by rumClient.initRum to forward timings to RUM. */
export function setWorkflowTimingSink(next: WorkflowTimingSink): void {
  sink = next;
}

export function startWorkflowTimer(workflow: string): void {
  starts.set(workflow, now());
}

/** Abandon a timer without emitting (e.g. the user navigated away). */
export function cancelWorkflowTimer(workflow: string): void {
  starts.delete(workflow);
}

/**
 * Complete a timer and emit. No-op if there was no matching start (so a stray
 * completion never emits a bogus 0ms timing).
 */
export function completeWorkflowTimer(workflow: string, outcome: string): void {
  const start = starts.get(workflow);
  if (start === undefined) {
    return;
  }
  starts.delete(workflow);
  const ms = now() - start;
  sink({
    workflowName: workflow,
    outcome,
    durationBucket: durationBucket(ms),
    durationMs: Math.round(ms),
  });
}

/** Stable workflow names (low-cardinality). */
export const WORKFLOWS = {
  LOGIN_TO_DASHBOARD: 'login_to_dashboard',
  PATIENT_WORKSPACE_LOAD: 'patient_workspace_load',
  TREATMENT_PLAN_SAVE: 'treatment_plan_save',
  NOTE_GENERATE: 'note_generate',
  NOTE_SIGN: 'note_sign',
  SESSION_RESULTS_LOAD: 'session_results_load',
  EMR_ACTION: 'emr_action',
} as const;

export type WorkflowName = (typeof WORKFLOWS)[keyof typeof WORKFLOWS];
